import ms from 'ms';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useLocalSettings } from '../../../ui/UseLocalSettings';
import { Emoji } from '../../../ui/emoji/Emoji';
import { EmojiData, EmojiDiversifications } from '../../../ui/emoji/EmojiModel';
import { EmojiStore } from '../../../ui/emoji/EmojiStore';
import { Copy, Flexer } from '../../dashboard/elements/LayoutElements';

import { Icon } from '../../../concepts/resources/Icon';
import { Colors, Input } from '../../../ui';
import { Tip } from '../../../ui/Tip';
import { ElementSelector2 } from './ElementSelector2';

export const useEmojiRecents = () => {

    // Keep now constant through the component lifetime
    const now = useMemo(() => Date.now(), []);

    const [raw, setRaw] = useLocalSettings('deqo/ui/emoji/recents', {} as Record<string, number>);
    const cleaned = useMemo(() =>
        Object.fromEntries(
            Object.entries(raw)
                .filter(([_, timestamp]) => now - timestamp < ms('30d'))
                .sort((a, b) => b[1] - a[1])
                .slice(0, 52)
        )
        , [raw]);

    const addRecent = useCallback((code: string) => {
        setRaw({ ...cleaned, [code]: Date.now() });
    }, [cleaned]);

    const clearRecents = useCallback(() => setRaw({}), [setRaw]);

    const recents = Object.entries(cleaned)
        .sort((a, b) => b[1] - a[1])
        .map(x => EmojiStore.find(x[0]))
        .filter(x => !!x);

    return { recents, addRecent, clearRecents };
};

export const useEmojiDiversity = () => useLocalSettings('deqo/ui/emoji/diversity', EmojiDiversifications[0].code);

export type EmojiDataPlus = EmojiData & { recent?: boolean };

const EmojiSelectorContext = React.createContext({
    selected: null as EmojiDataPlus | null,
    onSelect: (next: EmojiDataPlus, addToRecents: boolean) => { },
});

type EmojiGroupProps = {
    anchor: string,
    label: string,
    category: string,
};

type EmojiInstanceProps = {
    emoji: EmojiDataPlus,
    selected: boolean,
    onAction: () => void,
};

const EmojiInstance = React.memo(({ emoji, selected, onAction }: EmojiInstanceProps) =>
    <ElementSelector2.Item
        key={emoji.id}
        selected={selected}
        onAction={onAction}
    >
        <Flexer padding=".375">
            <Tip text={emoji.name} position="top">
                <span style={{ fontSize: '2rem', lineHeight: '2rem' }}>
                    <Emoji idOrCode={emoji.id} />
                </span>
            </Tip>
        </Flexer>
    </ElementSelector2.Item>
);

const EmojiRecent = React.memo(({ anchor, label }: Omit<EmojiGroupProps, 'category'>) => {
    const { selected, onSelect } = useContext(EmojiSelectorContext);
    const { recents, clearRecents } = useEmojiRecents();
    if (recents.length === 0) return null;

    return <ElementSelector2.Group anchor={anchor} label={label}>
        <Flexer flex="row" gap=".125" wrap="wrap">
            {recents.map(emoji => (
                <EmojiInstance
                    key={emoji.id}
                    emoji={{ ...emoji, recent: true }}
                    selected={selected?.id === emoji.id && selected.recent}
                    onAction={() => onSelect({ ...emoji, recent: true }, false)}
                />
            ))}
            <ElementSelector2.Item onAction={clearRecents}>
                <Flexer flex center padding=".375">
                    <Tip text="Clear Recents" position="top">
                        <Flexer padding=".25" border={`solid 1px ${Colors.border}`} borderRadius=".25">
                            <Icon src="deqo://icons/hero/trash" size="1.5" />
                        </Flexer>
                    </Tip>
                </Flexer>
            </ElementSelector2.Item>
        </Flexer>
    </ElementSelector2.Group>;
});

const EmojiCategory = React.memo<Omit<EmojiGroupProps, 'onSelect'>>(({ anchor, label, category }) => {
    const { selected, onSelect } = useContext(EmojiSelectorContext);
    // Filter emojis based on diversity
    const [diversity] = useEmojiDiversity();

    const emojis = useMemo(() => EmojiStore.getEmoji().filter(emoji =>
        emoji.category === category &&
        (!emoji.canDiversify || (!!diversity ? emoji.diversity?.includes(diversity) : !emoji.diversity?.length))
    ), [category, diversity]);

    return <ElementSelector2.Group anchor={anchor} label={label}>
        <Flexer flex="row" gap=".125" wrap="wrap">
            {emojis.map(emoji => (
                <EmojiInstance
                    key={emoji.id}
                    emoji={emoji}
                    selected={selected?.id === emoji.id && !selected.recent}
                    onAction={() => onSelect(emoji, true)}
                />
            ))}
        </Flexer>
    </ElementSelector2.Group>;
});

const diversityColorLookup: Record<string, string> = {
    '1f3fb': '#f7dece',
    '1f3fc': '#f3d2a2',
    '1f3fd': '#d5ab88',
    '1f3fe': '#af7e57',
    '1f3ff': '#7c533e',
    'default': '#ffcc4d'
};

const DiversityOption = styled.div<{ code?: string, active?: boolean }>`
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 50%;
    margin-right: 0.5rem;
    cursor: pointer;
    border: ${p => p.active ? '2px solid #007bff' : '1px solid #ccc'};
    background-color: ${p => diversityColorLookup[p.code || 'default']};
`;

export const EmojiDiversityView = React.memo(() => {
    const [diversity, setDiversity] = useEmojiDiversity();

    return <Flexer flex="row" align="center">
        {EmojiDiversifications.map(x => (
            <DiversityOption
                key={x.id}
                code={x.code}
                active={x.code === diversity}
                onClick={() => setDiversity(x.code)}
                title={x.id}
            />
        ))}
    </Flexer>;
});

const EmojiSearch = React.memo(({ search }: { search: string }) => {
    const { onSelect, selected } = useContext(EmojiSelectorContext);
    const [diversity] = useEmojiDiversity();
    const searchedEmojis = useMemo(() => {
        const searchTerm = search.toLowerCase()?.trim();
        if (!searchTerm) return [];
        return EmojiStore.getEmoji().filter(emoji =>
            (emoji.keywords?.some(keyword => keyword.toLowerCase().includes(searchTerm)) ||
                emoji.id.toLowerCase().includes(searchTerm) ||
                emoji.name.toLowerCase().includes(searchTerm) ||
                emoji.codes?.some(code => code.toLowerCase().includes(searchTerm))) &&
            (!emoji.canDiversify || (!!diversity ? emoji.diversity?.includes(diversity) : !emoji.diversity?.length))
        );
    }, [search, diversity]);

    return <ElementSelector2.Group anchor="search" label="Search Results">
        <Flexer flex="row" gap=".125" wrap="wrap">
            {searchedEmojis.map(emoji => (
                <EmojiInstance
                    key={emoji.id}
                    emoji={emoji}
                    selected={selected?.id === emoji.id && !selected.recent}
                    onAction={() => onSelect(emoji, true)}
                />
            ))}
            {searchedEmojis.length === 0 && (
                <Flexer width="100%" padding=".5" flex="row" align="center" justify="center">
                    <Copy flush color={Colors.textLightest} size=".75">
                        No matching emojis found.
                    </Copy>
                </Flexer>
            )}
        </Flexer>
    </ElementSelector2.Group>;
});

export type EmojiSelectorProps = {
    selected?: EmojiDataPlus,
    onSelect: (emoji: EmojiDataPlus) => void,
};

export const EmojiSelector = React.memo(({ selected, onSelect }: EmojiSelectorProps) => {
    const { addRecent } = useEmojiRecents();
    const [search, setSearch] = useState('');
    const clearSearch = useCallback(() => setSearch(''), [setSearch]);

    const onSelectInternal = useCallback((emoji: EmojiDataPlus, addToRecents: boolean) => {
        onSelect(emoji);
        if (addToRecents) {
            addRecent(emoji.id);
        }
    }, [onSelect, addRecent]);

    return <EmojiSelectorContext.Provider value={{ selected, onSelect: onSelectInternal }}>
        <ElementSelector2 name="emoji" contentWidth="26.125" height="32">
            <ElementSelector2.Menu>
                <ElementSelector2.MenuItem anchor="recent" label="Search" icon="deqo://icons/hero/search" iconSize="1.5" onSelect={clearSearch} />
                <ElementSelector2.MenuItem anchor="people" label="People" icon="deqo://icons/hero/user-circle" iconSize="1.5" onSelect={clearSearch} />
                <ElementSelector2.MenuItem anchor="nature" label="Nature" icon="deqo://icons/hero/globe-alt" iconSize="1.5" onSelect={clearSearch} />
                <ElementSelector2.MenuItem anchor="food" label="Food" icon="deqo://icons/hero/cake" iconSize="1.5" onSelect={clearSearch} />
                <ElementSelector2.MenuItem anchor="activity" label="Activity" icon="deqo://icons/hero/puzzle" iconSize="1.5" onSelect={clearSearch} />
                <ElementSelector2.MenuItem anchor="travel" label="Travel" icon="deqo://icons/hero/map" iconSize="1.5" onSelect={clearSearch} />
                <ElementSelector2.MenuItem anchor="objects" label="Objects" icon="deqo://icons/hero/light-bulb" iconSize="1.5" onSelect={clearSearch} />
                <ElementSelector2.MenuItem anchor="symbols" label="Symbols" icon="deqo://icons/hero/heart" iconSize="1.5" onSelect={clearSearch} />
                <ElementSelector2.MenuItem anchor="flags" label="Flags" icon="deqo://icons/hero/flag" iconSize="1.5" onSelect={clearSearch} />
            </ElementSelector2.Menu>
            <Flexer padding=".5">
                <Input
                    autoFocus
                    value={search}
                    placeholder="Search"
                    onChange={setSearch}
                />
            </Flexer>
            <ElementSelector2.Content>
                {search && <EmojiSearch search={search} />}
                {!search && <>
                    <EmojiRecent anchor="recent" label="Recently Used" />
                    <EmojiCategory anchor="people" label="People" category="people" />
                    <EmojiCategory anchor="nature" label="Nature" category="nature" />
                    <EmojiCategory anchor="food" label="Food" category="food" />
                    <EmojiCategory anchor="activity" label="Activity" category="activity" />
                    <EmojiCategory anchor="travel" label="Travel" category="travel" />
                    <EmojiCategory anchor="objects" label="Objects" category="objects" />
                    <EmojiCategory anchor="symbols" label="Symbols" category="symbols" />
                    <EmojiCategory anchor="flags" label="Flags" category="flags" />
                </>}
            </ElementSelector2.Content>
            <ElementSelector2.Footer>
                <EmojiDiversityView />
            </ElementSelector2.Footer>
        </ElementSelector2>
    </EmojiSelectorContext.Provider>;
});
